import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isAiGenerated, isReportedIncorrect } from 'app/services/PPE/ppe.models';
import { PpeService } from 'app/services/PPE/ppe.service';
import { PpeReportDialogComponent, PpeReportDialogResult } from '../ppe-report-dialog.component';

/** The observation fields the report action reads (observation details payload). */
export interface PpeReportableObservation {
  id?: string | number | null;
  is_ai_generated?: unknown;
  deleted_at?: unknown;
  reported_incorrect?: unknown;
}

/**
 * "Incorrect Detection" action for AI PPE observations (V2CORE-3078, V2CORE-3361).
 *
 * Shows the button for an AI observation that is not deleted and not reported yet, or a
 * "Reported" chip once this user reported it. The button opens the PPE report dialog and
 * sends `POST v2/observations/{id}/detection-feedback`. Non-AI observations show nothing.
 *
 * One component for every V1 place that shows observation details: the observation
 * side panel (`app-observation-info`) and the shared details view used by "Open as page"
 * and the 360 / floor popups (`app-shared-observation-details`).
 *
 * No permission check: anyone who can view the observation may report it (HOME answers
 * 403 otherwise). The observation itself is not changed, so hosts need no handler.
 */
@Component({
  selector: 'app-ppe-ai-report-action',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      *ngIf="isReported || canReport"
      class="ppe-ai-report flex flex-row flex-wrap items-center gap-2"
    >
      <span
        *ngIf="isReported"
        class="ppe-ai-reported inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-gray-100 text-secondary dark:bg-gray-800"
        data-testid="ppe-ai-reported"
      >
        <mat-icon class="icon-size-4" aria-hidden="true">check</mat-icon>
        {{ 'ppe.ai.report.reported' | transloco }}
      </span>
      <button
        *ngIf="canReport"
        type="button"
        mat-stroked-button
        color="warn"
        class="ppe-ai-report-button mat-dense text-smaller"
        data-testid="ppe-ai-report-button"
        [disabled]="isReporting"
        (click)="openReportDialog()"
      >
        <mat-icon class="icon-size-4" aria-hidden="true">report</mat-icon>
        <span class="ms-1">{{ 'ppe.ai.report.button' | transloco }}</span>
      </button>
    </div>
  `,
})
export class PpeAiReportActionComponent implements OnDestroy {
  /** The observation shown by the host; null while it loads. */
  @Input() observation: PpeReportableObservation | null | undefined;

  /** Emits the observation id after a report was accepted (new or already reported). */
  @Output() reported = new EventEmitter<string | number>();

  /** A report is being sent. */
  isReporting = false;

  /**
   * The report dialog is open. A flag, not `MatDialog.openDialogs`, because some hosts
   * (the 360 / floor popups) are dialogs themselves.
   */
  private isDialogOpen = false;

  /**
   * Id of the observation this user reported here. Kept by id so a host that swaps in
   * another observation does not show that one as reported.
   */
  private reportedObservationId: string | number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private ppeService: PpeService,
    private toaster: ToastrService,
    private transloco: TranslocoService,
    private cdr: ChangeDetectorRef,
  ) {}

  /** The observation was created by PPE auto-detection. */
  get isAiObservation(): boolean {
    return isAiGenerated(this.observation);
  }

  /** The current user already reported this AI observation as incorrect. */
  get isReported(): boolean {
    if (!this.isAiObservation) {
      return false;
    }
    const id = this.observation?.id;
    return (
      isReportedIncorrect(this.observation) ||
      (id !== undefined && id !== null && id === this.reportedObservationId)
    );
  }

  /** Show "Incorrect Detection": AI, has an id, not deleted, not reported yet. */
  get canReport(): boolean {
    return (
      this.isAiObservation &&
      !!this.observation?.id &&
      !this.observation?.deleted_at &&
      !this.isReported
    );
  }

  /** Ask for a reason and send the report. A second click while one is open does nothing. */
  openReportDialog(): void {
    if (!this.canReport || this.isReporting || this.isDialogOpen) {
      return;
    }
    const observationId = this.observation.id as string | number;
    this.isDialogOpen = true;
    this.dialog
      .open<PpeReportDialogComponent, void, PpeReportDialogResult>(PpeReportDialogComponent, {
        width: '480px',
        maxWidth: '95vw',
        autoFocus: false,
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isDialogOpen = false;
        if (result) {
          this.send(observationId, result);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private send(observationId: string | number, result: PpeReportDialogResult): void {
    this.isReporting = true;
    this.cdr.markForCheck();
    this.ppeService
      .reportIncorrect(observationId, result.reasonCode, result.justification)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isReporting = false;
          this.reportedObservationId = observationId;
          this.toaster.success(
            this.transloco.translate(
              res?.already_reported ? 'ppe.ai.report.already_reported' : 'ppe.ai.report.success',
            ),
          );
          this.reported.emit(observationId);
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.isReporting = false;
          this.toaster.error(this.errorMessage(err));
          this.cdr.markForCheck();
        },
      });
  }

  private errorMessage(err: HttpErrorResponse): string {
    // 403 = the user may not view the observation; 422 = not AI-generated or deleted.
    if (err?.status === 403) {
      return this.transloco.translate('ppe.ai.report.error_forbidden');
    }
    if (err?.status === 422) {
      return this.transloco.translate('ppe.ai.report.error_not_reportable');
    }
    return this.transloco.translate('ppe.ai.report.error');
  }
}
